import { useState } from 'react';

export default function SOP() {
  const [activeTab, setActiveTab] = useState<'house' | 'grave'>('house');

  const houseSteps = [
    { number: '01', title: '문의 및 위성 분석', description: '10년치 위성 사진으로 정원 지형 및 식생 변화 분석', icon: 'ri-satellite-line' },
    { number: '02', title: 'GPS 견적 발송', description: '21개 요인 적용한 투명 견적서 발송', icon: 'ri-file-text-line' },
    { number: '03', title: '현장 출동 및 작업', description: '고압 세척기, 엔진톱 등 전문 장비 투입', icon: 'ri-tools-fill' },
    { number: '04', title: '전문 조경 처방', description: '수목 전정, 잡초 재생 방지 처리로 품질 차별화', icon: 'ri-plant-line' },
    { number: '05', title: '디지털 리포트', description: '드론 촬영 전/후 사진 및 전문가 소견 즉시 전송', icon: 'ri-file-chart-line' },
  ];

  const graveSteps = [
    { number: '01', title: '문의 및 위성 분석', description: '10년치 위성 사진으로 선산 지형 및 면적 기초 데이터 산출', icon: 'ri-satellite-line' },
    { number: '02', title: 'GPS 견적 발송', description: '21개 요인 적용한 투명 견적서 발송', icon: 'ri-file-text-line' },
    { number: '03', title: '현장 출동 및 벌초', description: '고압 세척기(석물 세척), 엔진톱(잡목 제거) 전문 장비 투입', icon: 'ri-tools-fill' },
    { number: '04', title: '전문 처방', description: '예초 후 밑동 재생 방지 처리로 서비스 품질 차별화', icon: 'ri-plant-line' },
    { number: '05', title: '디지털 리포트', description: '드론 촬영 전/후 사진 및 전문가 소견 즉시 전송', icon: 'ri-file-chart-line' },
  ];

  const currentSteps = activeTab === 'house' ? houseSteps : graveSteps;

  return (
    <section id="sop" className="relative py-16 sm:py-28 px-4 sm:px-6 overflow-hidden" style={{background: 'linear-gradient(145deg, #111e3a 0%, #0d1f3c 60%, #152a52 100%)'}}>
      <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'linear-gradient(115deg, transparent 44%, #c9a227 45%, #c9a227 45.3%, transparent 46%), linear-gradient(70deg, transparent 62%, #c9a227 63%, #c9a227 63.3%, transparent 64%)', backgroundSize: '320px 320px'}}></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white mb-4 sm:mb-5 leading-[1.2] tracking-tight">
            표준 운영 절차
          </h2>
          <p className="text-base sm:text-xl text-white/70 leading-relaxed">
            디지털 기반 5단계 프로세스로 완벽한 서비스 제공
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-8 sm:mb-18">
          <div className="inline-flex rounded-full p-1 shadow-sm border border-[#c9a227]/20" style={{background: 'rgba(13,31,60,0.8)'}}>
            <button
              onClick={() => setActiveTab('house')}
              className={`px-5 sm:px-8 py-2.5 sm:py-3 rounded-full text-sm sm:text-base font-medium transition-all whitespace-nowrap cursor-pointer ${activeTab === 'house' ? 'text-[#0d1f3c] font-bold' : 'text-white/70 hover:text-white'}`}
              style={activeTab === 'house' ? {background: 'linear-gradient(135deg, #e8c84a, #b8860b)'} : {}}
            >
              전원주택 관리
            </button>
            <button
              onClick={() => setActiveTab('grave')}
              className={`px-5 sm:px-8 py-2.5 sm:py-3 rounded-full text-sm sm:text-base font-medium transition-all whitespace-nowrap cursor-pointer ${activeTab === 'grave' ? 'text-[#0d1f3c] font-bold' : 'text-white/70 hover:text-white'}`}
              style={activeTab === 'grave' ? {background: 'linear-gradient(135deg, #e8c84a, #b8860b)'} : {}}
            >
              산소·선산 관리
            </button>
          </div>
        </div>

        {/* Process Steps */}
        <div className="flex flex-col sm:grid sm:grid-cols-5 gap-2 sm:gap-5">
          {currentSteps.map((step, index) => (
            <div key={index} className="relative flex sm:block items-stretch gap-0">
              {/* Card */}
              <div className="rounded-xl sm:rounded-2xl px-4 py-3 sm:p-7 flex-1 border border-[#c9a227]/20 hover:border-[#c9a227]/40 transition-colors flex items-center gap-3 sm:block" style={{background: 'linear-gradient(160deg, rgba(30,58,110,0.7), rgba(13,31,60,0.85))'}}>
                {/* Mobile: number badge only (no icon) */}
                <div className="sm:hidden w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full border border-[#c9a227]/40 bg-[#c9a227]/10">
                  <span className="text-xs font-bold text-[#c9a227]">{step.number}</span>
                </div>
                {/* PC: big number */}
                <div className="hidden sm:block text-5xl font-bold mb-5 leading-none text-[#c9a227]/25">{step.number}</div>
                {/* Icon: PC only */}
                <div className="hidden sm:flex w-12 h-12 items-center justify-center mb-5 flex-shrink-0">
                  <i className={`${step.icon} text-4xl text-[#c9a227]`}></i>
                </div>
                <div className="flex-1 sm:w-full min-w-0">
                  <h3 className="text-sm sm:text-lg font-bold text-white sm:mb-3 leading-snug tracking-tight">{step.title}</h3>
                  <p className="text-xs text-white/60 leading-relaxed hidden sm:block">{step.description}</p>
                </div>
              </div>
              {/* Arrow */}
              {index < currentSteps.length - 1 && (
                <>
                  <div className="hidden sm:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-2xl text-[#c9a227]"></i>
                    </div>
                  </div>
                  <div className="sm:hidden flex justify-center py-0.5">
                    <i className="ri-arrow-down-line text-base text-[#c9a227]/50"></i>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
